/**
 * Layout density: detected from the device, or forced.
 *
 * **The default is the full desktop application.** Compact is chosen only when the device is
 * actually a phone or tablet — not when a window happens to be narrow. Somebody working in a
 * half-width window on a PC came for the full board, and taking columns away from them because
 * they resized a window is worse than a horizontal scrollbar.
 *
 * # How "is this a mobile device" is decided, and why not by user agent alone
 *
 * User agent strings lie, and increasingly by design. iPadOS reports itself as macOS. Every
 * browser has a "Request desktop site" switch that rewrites the string. Chromium is actively
 * freezing the parts people parse. A UA regex written today is wrong somewhere already.
 *
 * So the check runs strongest signal first:
 *
 * 1. **`navigator.userAgentData.mobile`** — a browser telling us directly, via Client Hints.
 *    Authoritative where it exists; Chromium only, so it cannot be the whole answer.
 * 2. **Input capability** — `pointer: coarse` and `hover: none` together. This asks what the
 *    device can *do* rather than what it calls itself, which is the question that actually
 *    matters: a finger cannot hover, and it cannot hit a 24px target reliably. Catches iPadOS
 *    correctly despite the UA, and a laptop in tablet mode correctly reports coarse while docked
 *    with a mouse reports fine.
 * 3. **User agent** — last resort, for browsers with neither of the above.
 *
 * Deliberately **not** device fingerprinting. Canvas and font entropy identify a *person* across
 * sites; that is a tracking technique, it is disproportionate for choosing a stylesheet, and it
 * would sit badly in a product whose argument is that it holds less of a client's data than the
 * alternatives.
 *
 * # What gets published
 *
 * - `data-layout` — what the person chose: `auto`, `desktop`, or `compact`.
 * - `data-layout-effective` — what that resolves to: `desktop` or `compact`.
 *
 * **CSS keys on `data-layout-effective`, never on a width media query for these rules.** A media
 * query cannot be overridden by a preference without writing every rule twice, and the second copy
 * is the one that rots.
 */

export const LAYOUT_MODES = ['auto', 'desktop', 'compact'] as const;

export type LayoutMode = (typeof LAYOUT_MODES)[number];

/** What a mode resolves to. Only ever these two — the CSS has no third case. */
export type EffectiveLayout = 'desktop' | 'compact';

export const LAYOUT_STORAGE_KEY = 'conduit.layout';

export interface LayoutOption {
  readonly id: LayoutMode;
  readonly label: string;
  readonly description: string;
}

export const LAYOUT_OPTIONS: readonly LayoutOption[] = [
  {
    id: 'auto',
    label: 'Automatic',
    description: 'Full desktop everywhere except on a phone or tablet.',
  },
  {
    id: 'desktop',
    label: 'Full desktop',
    description: 'Every column, at any size. Scrolls sideways rather than hiding anything.',
  },
  {
    id: 'compact',
    label: 'Compact',
    description: 'Drawer navigation and stacked forms, even on a large screen.',
  },
];

/**
 * The user agent fallback.
 *
 * Only consulted when the two better signals are unavailable. `Android` is paired with `Mobile`
 * because Android tablets omit it, and a tablet with a keyboard is closer to a desktop than to a
 * phone. `iPad` is listed for older iPadOS; current versions are caught by the pointer check
 * above, since they no longer admit to being an iPad at all.
 */
const MOBILE_UA = /iPhone|iPod|iPad|Android.*Mobile|Windows Phone|BlackBerry|Opera Mini|IEMobile/i;

interface UserAgentData {
  readonly mobile?: boolean;
}

/** Whether this is a phone or tablet, as opposed to a small window. */
export function isMobileDevice(): boolean {
  // 1. The browser saying so outright.
  const data = (navigator as Navigator & { userAgentData?: UserAgentData }).userAgentData;
  if (typeof data?.mobile === 'boolean') {
    return data.mobile;
  }

  // 2. What the device can do. A finger cannot hover and cannot hit a small target; a mouse can
  //    do both. This is the signal that gets iPadOS right despite its user agent.
  try {
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const cannotHover = window.matchMedia('(hover: none)').matches;
    if (coarse && cannotHover) {
      return true;
    }
    // A touchscreen laptop reports coarse while being used by hand, but still hovers with its
    // trackpad. Both must hold, so it correctly stays on the desktop layout.
    if (window.matchMedia('(any-pointer: fine)').matches) {
      return false;
    }
  } catch {
    // matchMedia missing or throwing. Fall through to the user agent.
  }

  // 3. Last resort.
  return MOBILE_UA.test(navigator.userAgent);
}

function isMode(value: string | null): value is LayoutMode {
  return value !== null && (LAYOUT_MODES as readonly string[]).includes(value);
}

/** The stored preference, or `auto`. */
export function storedLayoutMode(): LayoutMode {
  try {
    const raw = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
    return isMode(raw) ? raw : 'auto';
  } catch {
    // Private browsing, or storage disabled. Not worth failing over: the default is correct.
    return 'auto';
  }
}

/** What a mode means on this device. */
export function resolveLayout(mode: LayoutMode): EffectiveLayout {
  if (mode === 'desktop') return 'desktop';
  if (mode === 'compact') return 'compact';
  // `auto` — and note that width is not consulted. A narrow window on a PC keeps the full
  // application, which is the whole point of the default.
  return isMobileDevice() ? 'compact' : 'desktop';
}

/** Writes both attributes onto the root element. */
export function applyLayout(mode: LayoutMode): EffectiveLayout {
  const effective = resolveLayout(mode);
  const root = document.documentElement;
  root.setAttribute('data-layout', mode);
  root.setAttribute('data-layout-effective', effective);
  return effective;
}

/** Persists a choice and applies it. */
export function setLayoutMode(mode: LayoutMode): EffectiveLayout {
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, mode);
  } catch {
    // Applying it still works for this session, which is the part that matters.
  }
  return applyLayout(mode);
}

/**
 * Re-resolves when the device's input capability changes.
 *
 * Rare but real: a Surface undocked from its keyboard, or an iPad with a trackpad connected. The
 * pointer media queries change, and the layout should follow rather than staying on whatever was
 * true at load.
 *
 * Returns an unsubscribe function.
 */
export function followDeviceCapability(onChange: (effective: EffectiveLayout) => void): () => void {
  let queries: MediaQueryList[] = [];

  const handle = (): void => {
    onChange(applyLayout(storedLayoutMode()));
  };

  try {
    queries = [window.matchMedia('(pointer: coarse)'), window.matchMedia('(hover: none)')];
    for (const query of queries) {
      query.addEventListener('change', handle);
    }
  } catch {
    // No matchMedia: nothing to follow, and the load-time answer stands.
  }

  return (): void => {
    for (const query of queries) {
      query.removeEventListener('change', handle);
    }
  };
}
